import type { TFunction } from "i18next";

/**
 * Format a timestamp as a relative time string
 */
export function formatRelativeTime(timestamp: number, t?: TFunction): string {
  const now = Date.now();
  const diff = now - timestamp;

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) {
    return t ? t("common:time.justNow") : "Just now";
  }

  if (diff < hour) {
    const minutes = Math.floor(diff / minute);
    return t
      ? t("common:time.minutesAgo", { count: minutes })
      : `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  if (diff < day) {
    const hours = Math.floor(diff / hour);
    return t
      ? t("common:time.hoursAgo", { count: hours })
      : `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const date = new Date(timestamp);
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if timestamp was yesterday (same calendar day)
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    const timeStr = `${displayHours}:${displayMinutes} ${period}`;
    return t
      ? t("common:time.yesterdayAt", { time: timeStr })
      : `Yesterday at ${timeStr}`;
  }

  // Older dates - return formatted date string
  const lang = t ? undefined : "en-US";
  return date.toLocaleDateString(lang, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
}
